import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import {
  ActiveInactiveOffer,
  deleteOffer,
  getAllOffers,
} from "./services/offer.services";
import { clearOfferID, setOfferID } from "./offerSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import AvatarComponent from "../../components/common/AvatarComponent";

const Offer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllOffers());
    return () => {
      dispatch(closeDialog());
    };
  }, [dispatch]);

  return (
    <>
      <DeleteOfferDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Offer List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={() => navigate("/offer/create")}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New Offer
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <OfferGrid />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const OfferGrid: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef<any>(null);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const loading = useAppSelector((state) => state.offer.loading);
  const OfferList = useAppSelector((state) => state.offer.OfferList);

  const [page, setPage] = useState({ skip: 0, take: 10 });
  const [filter, setFilter] = useState<CompositeFilterDescriptor>();
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >(10);

  const pageChange = (event: GridPageChangeEvent) => {
    let targetEvent = event.syntheticEvent as PagerTargetEvent;
    setPageSizeValue(targetEvent.target.value);
    setPage({
      skip: event.page.skip,
      take: event.page.take,
    });
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: 10 });
  };

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => {
    return (
      <HeaderThElement
        columnId={props.thProps?.columnId || ""}
        {...props.thProps}
        className="table-header"
      >
        {props.children}
      </HeaderThElement>
    );
  };

  const handleDeleteAction = (ID: number) => {
    dispatch(openDialog("deleteOffer"));
    dispatch(setOfferID(ID));
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() =>
              navigate("/offer/edit", { state: { OfferID: props.dataItem.ID } })
            }
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleDeleteAction(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveOffer(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllOffers());
        } else {
          dispatch(getAllOffers());
        }
      });
    };

    return (
      <td style={{ textAlign: "center" }}>
        <Switch
          disabled={isEditAccess ? false : true}
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}

      {OfferList && OfferList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          className="responsive-table"
          data={
            filter
              ? filterBy(OfferList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : OfferList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={OfferList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="Images"
            title="Images"
            width={75}
            filterable={false}
            cell={(props: GridCellProps) => (
              <td>
                <AvatarComponent
                  image={props.dataItem?.Images}
                  title={props.dataItem?.Title}
                />
              </td>
            )}
          />
          <Column
            field="Title"
            title="Title"
            width={300}
            cell={(props: GridCellProps) => (
              <td>
                <div>
                  {isEditAccess ? (
                    <div
                      className="hoverableName"
                      onClick={() =>
                        navigate("/offer/edit", {
                          state: { OfferID: props.dataItem?.ID },
                        })
                      }
                    >
                      {props.dataItem?.Title}
                    </div>
                  ) : (
                    <div className="normalName">{props.dataItem?.Title}</div>
                  )}
                </div>
              </td>
            )}
          />

          <Column field="ShortDescription" title="Short Description" />

          <Column
            field="IsDisplayHome"
            title="Display Home"
            width={150}
            cell={(props: GridCellProps) => (
              <td>{props.dataItem?.IsDisplayHome ? "Yes" : "No"}</td>
            )}
          />

          <Column
            field="ID"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          />

          {(isEditAccess || isDeleteAccess) && (
            <Column
              title="Action"
              width={110}
              cell={MyEditCommandCell}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteOfferDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const OfferID = useAppSelector((state) => state.offer.OfferID);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteOffer(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getAllOffers());
        dispatch(closeDialog());
        dispatch(clearOfferID());
      } else {
        dispatch(getAllOffers());
        dispatch(closeDialog());
        dispatch(clearOfferID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteOffer" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Offer?</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Offer?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(OfferID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Offer;
